import { useEffect, useMemo, useState } from 'react'
import { ChevronDown } from 'lucide-react'
import type { FacetResponse } from './types'
import type { RenderContext } from './context'
import { bucketIndex, FilterSection } from './FacetCheckboxGroup'

// Subcategory lists with more than this many items become scrollable.
const SUBCATEGORY_SCROLL_THRESHOLD = 8

export function CategorySection({ ctx }: { ctx: RenderContext }) {
  const { categories, facets, category, subcategory, setParam, searchParams, setSearchParams } =
    ctx
  const counts = bucketIndex(facets?.categories)

  return (
    <FilterSection title="Categorie" defaultOpen>
      <button
        onClick={() => setParam('category', '')}
        className={`text-sm py-1 px-2 rounded text-left w-full ${!category ? 'bg-primary text-white font-medium' : 'text-neutral-600 hover:bg-neutral-100'}`}
      >
        Alle producten
      </button>
      {categories?.map((cat) => {
        const count = facets ? (counts.get(cat.name) ?? 0) : cat.product_count
        const disabled = facets ? count === 0 && category !== cat.name : false
        return (
          <CategoryTreeItem
            key={cat.name}
            name={cat.name}
            count={count}
            disabled={disabled}
            facets={facets}
            isActive={category === cat.name}
            activeSubcategory={category === cat.name ? subcategory : ''}
            onSelect={(catName) => setParam('category', catName)}
            onSelectSub={(subName) => {
              const next = new URLSearchParams(searchParams)
              next.set('category', cat.name)
              next.set('subcategory', subName)
              next.delete('page')
              setSearchParams(next)
            }}
          />
        )
      })}
    </FilterSection>
  )
}

function CategoryTreeItem({
  name,
  count,
  disabled,
  facets,
  isActive,
  activeSubcategory,
  onSelect,
  onSelectSub,
}: {
  name: string
  count: number
  disabled: boolean
  facets: FacetResponse | undefined
  isActive: boolean
  activeSubcategory: string
  onSelect: (name: string) => void
  onSelectSub: (name: string) => void
}) {
  // Sync expanded state with isActive so selecting a category immediately
  // reveals its subcategories without a second click. The user can still
  // collapse/expand manually after that.
  const [expanded, setExpanded] = useState(isActive)
  useEffect(() => {
    if (isActive) setExpanded(true)
  }, [isActive])

  // Only the ACTIVE category shows real subcategories — sourced from the live
  // facet response, which reflects actual product counts for the current
  // filter set. Inactive categories show no children: the per-category tree
  // data (cat.subcategories) contains series/MPN prefixes that are not linked
  // to any products and would produce a wall of zero-count junk.
  const facetSubs = useMemo(
    () => (isActive ? (facets?.subcategories ?? []) : []),
    [isActive, facets],
  )
  const hasSubcategories = isActive && facetSubs.length > 0
  // Long lists are collapsed to a "Show all" toggle (WEB-401) rather than a
  // scrollbar, which hid items and was hard to discover.
  const collapsible = facetSubs.length > SUBCATEGORY_SCROLL_THRESHOLD
  const [showAllSubs, setShowAllSubs] = useState(false)
  const visibleSubs =
    collapsible && !showAllSubs ? facetSubs.slice(0, SUBCATEGORY_SCROLL_THRESHOLD) : facetSubs

  return (
    <div>
      <div className="flex items-center gap-0.5">
        {hasSubcategories ? (
          // Expand/collapse button — only shown on the active category, where
          // we have real facet-backed subcategories to show or hide.
          <button
            onClick={() => setExpanded((e) => !e)}
            className={`flex-shrink-0 p-1 rounded hover:bg-neutral-100 transition-colors text-primary`}
            aria-label={expanded ? 'Inklappen' : 'Uitklappen'}
          >
            <ChevronDown
              className={`h-3.5 w-3.5 transition-transform ${expanded ? '' : '-rotate-90'}`}
            />
          </button>
        ) : (
          // Spacer keeps label indentation consistent across items.
          <span className="flex-shrink-0 w-[1.375rem]" />
        )}
        <button
          onClick={() => onSelect(isActive ? '' : name)}
          disabled={disabled && !isActive}
          className={`text-sm py-1 px-2 rounded text-left flex-1 min-w-0 flex items-center justify-between gap-2 ${
            isActive && !activeSubcategory
              ? 'bg-primary text-white font-medium'
              : disabled && !isActive
                ? 'text-neutral-300 cursor-not-allowed'
                : 'text-neutral-600 hover:bg-neutral-100'
          }`}
        >
          <span className="truncate">{name}</span>
          <span
            className={`text-xs tabular-nums flex-shrink-0 ${
              isActive && !activeSubcategory ? 'text-white/80' : 'text-neutral-400'
            }`}
          >
            {count}
          </span>
        </button>
      </div>

      {expanded && hasSubcategories && (
        <div className="ml-6 mt-0.5 flex flex-col gap-0.5">
          {visibleSubs.map((sub) => {
            // A currently-selected subcategory stays clickable even at count=0
            // so the user can un-select it — same pattern as other filter sections.
            const subDisabled = sub.count === 0 && activeSubcategory !== sub.value
            return (
              <button
                key={sub.value}
                disabled={subDisabled}
                onClick={() => onSelectSub(sub.value)}
                className={`text-xs py-1 px-2 rounded text-left flex items-center justify-between gap-2 ${
                  activeSubcategory === sub.value
                    ? 'bg-primary/10 text-primary font-medium'
                    : subDisabled
                      ? 'text-neutral-300 cursor-not-allowed'
                      : 'text-neutral-600 hover:bg-neutral-50'
                }`}
              >
                <span className="truncate">{sub.value}</span>
                <span
                  className={`tabular-nums flex-shrink-0 ${
                    activeSubcategory === sub.value ? 'text-primary/70' : 'text-neutral-400'
                  }`}
                >
                  {sub.count}
                </span>
              </button>
            )
          })}
          {collapsible && (
            <button
              type="button"
              onClick={() => setShowAllSubs((v) => !v)}
              className="text-xs py-1 px-2 mt-0.5 rounded text-left font-medium text-primary hover:bg-neutral-50"
            >
              {showAllSubs ? 'Toon minder' : `Toon alle (${facetSubs.length})`}
            </button>
          )}
        </div>
      )}
    </div>
  )
}
